import { ReactElement, useEffect, useRef, useState } from 'react';
import Plotly from 'plotly.js-dist-min';

const COLORS = {
  combined: '#f97316',
  slope: '#10b981',
};

interface CombinedSeries {
  Temp_TG5?: number[];
  Crr_xG3?: number[];
  x_series?: number[];
  y_series?: number[];
  x?: number[];
  y?: number[];
  weight?: number[];
  x_name?: string;
  y_name?: string;
  slope?: number | null;
  intercept?: number | null;
  y_fit_series?: number[];
  y_fit?: number[];
}

interface CrrTempCombinedPlotProps {
  combined?: CombinedSeries | null;
  slope?: CombinedSeries | null;
  height?: number;
  isPercentage?: boolean;
}

const pickSeries = (series?: CombinedSeries | null, axis: 'x' | 'y' = 'x'): number[] => {
  if (!series) return [];

  if (axis === 'x') {
    if (Array.isArray(series.Temp_TG5)) return series.Temp_TG5;
    if (Array.isArray(series.x_series)) return series.x_series;
    if (Array.isArray(series.x)) return series.x;
    return [];
  }

  if (Array.isArray(series.Crr_xG3)) return series.Crr_xG3;
  if (Array.isArray(series.y_series)) return series.y_series;
  if (Array.isArray(series.y)) return series.y;
  return [];
};

const pickFitSeries = (series?: CombinedSeries | null): number[] => {
  if (!series) return [];
  if (Array.isArray(series.y_fit_series)) return series.y_fit_series;
  if (Array.isArray(series.y_fit)) return series.y_fit;
  return [];
};

const buildPoints = (
  series?: CombinedSeries | null,
  valueSelector: (s?: CombinedSeries | null) => number[] = (s) => pickSeries(s, 'y'),
) => {
  if (!series) return [];
  const temps = pickSeries(series, 'x');
  const crrs = valueSelector(series);
  const weights = Array.isArray(series.weight) ? series.weight : [];

  const count = Math.min(temps.length, crrs.length);

  return Array.from({ length: count })
    .map((_, idx) => ({
      temp: temps[idx],
      crr: crrs[idx],
      weight: weights[idx],
    }))
    .filter(({ temp, crr }) =>
      temp !== null && temp !== undefined &&
      crr !== null && crr !== undefined
    )
    .sort((a, b) => a.temp - b.temp);
};

export default function CrrTempCombinedPlot({
  combined,
  slope,
  height = 420,
  isPercentage = false,
}: CrrTempCombinedPlotProps): ReactElement {
  const plotRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const plotElement = plotRef.current;
    if (!plotElement) {
      return undefined;
    }

    const combinedPoints = buildPoints(combined);
    const slopePoints = buildPoints(slope, pickFitSeries);

    if (!combinedPoints.length && !slopePoints.length) {
      Plotly.purge(plotElement);
      setIsLoading(false);
      return undefined;
    }

    setIsLoading(true);

    const traces: any[] = [];

    if (combinedPoints.length) {
      traces.push({
        x: combinedPoints.map((p) => p.temp),
        y: combinedPoints.map((p) => p.crr),
        mode: 'markers',
        type: 'scatter',
        name: 'Combined',
        marker: {
          color: COLORS.combined,
          size: 9,
          opacity: 0.9,
          line: { width: 1, color: '#fff' },
        },
        customdata: combinedPoints.map((p) => p.weight),
        hovertemplate:
          '<b>Combined</b><br>' +
          'Temperature: %{x:.1f} °C<br>' +
          (isPercentage ? 'Crr % Change: %{y:.4f}<br>' : 'Crr: %{y:.6f}<br>') +
          (combinedPoints.some((p) => p.weight !== null && p.weight !== undefined)
            ? 'Weight: %{customdata:.3f}<br>'
            : '') +
          '<extra></extra>',
      });
    }

    if (slopePoints.length) {
      traces.push({
        x: slopePoints.map((p) => p.temp),
        y: slopePoints.map((p) => p.crr),
        mode: 'lines',
        type: 'scatter',
        name: 'Slope Fit',
        line: { color: COLORS.slope, width: 2, dash: 'dash' },
        hovertemplate:
          '<b>Slope Fit</b><br>' +
          'Temperature: %{x:.1f} °C<br>' +
          (isPercentage ? 'Crr % Change: %{y:.4f}<br>' : 'Crr: %{y:.6f}<br>') +
          '<extra></extra>',
      });
    }

    const temps = [...combinedPoints, ...slopePoints]
      .map((p) => p.temp).filter((v) => v != null && isFinite(v));
    const values = [...combinedPoints, ...slopePoints]
      .map((p) => p.crr).filter((v) => v != null && isFinite(v));
    const minTemp = temps.length ? Math.min(...temps) : undefined;
    const maxTemp = temps.length ? Math.max(...temps) : undefined;
    const padding = minTemp !== undefined && maxTemp !== undefined
      ? Math.max((maxTemp - minTemp) * 0.05, 1)
      : 1;
    const minValue = values.length ? Math.min(...values) : undefined;
    const maxValue = values.length ? Math.max(...values) : undefined;
    const valuePadding = minValue !== undefined && maxValue !== undefined
      ? Math.max((maxValue - minValue) * 0.05, isPercentage ? 0.001 : 0.00001)
      : (isPercentage ? 0.001 : 0.00001);

    const annotations: any[] = [];
    const slopeValue = typeof slope?.slope === 'number' ? slope.slope : undefined;
    if (slopeValue !== undefined) {
      annotations.push({
        text: `Slope = ${slopeValue.toExponential(3)}`,
        xref: 'paper',
        yref: 'paper',
        x: 0.98,
        y: 1.08,
        xanchor: 'right',
        showarrow: false,
        font: { size: 12, color: '#374151' },
        align: 'right',
      });
    }

    const layout: Partial<Plotly.Layout> = {
      title: {
        text: isPercentage ? 'Crr % Change vs Temperature' : 'Crr vs Temperature',
        font: { family: 'Poppins', size: 16 },
      },
      xaxis: {
        title: {
          text: 'Temperature °C',
          font: { family: 'Poppins' },
        },
        type: 'linear',
        range:
          minTemp !== undefined && maxTemp !== undefined
            ? [minTemp - padding, maxTemp + padding]
            : undefined,
        autorange: minTemp === undefined,
      },
      yaxis: {
        title: {
          text: isPercentage ? 'Crr % Change' : 'Crr',
          font: { family: 'Poppins' },
        },
        range:
          minValue !== undefined && maxValue !== undefined
            ? [minValue - valuePadding, maxValue + valuePadding]
            : undefined,
        autorange: minValue === undefined,
      },
      legend: {
        orientation: 'h',
        x: 0.5,
        xanchor: 'center',
        y: -0.20,
        yanchor: 'top',
        bgcolor: 'rgba(255,255,255,0.85)',
        entrywidth: 0.3,
        entrywidthmode: 'fraction',
      },
      margin: { l: 70, r: 30, t: 70, b: 60 },
      hovermode: 'closest',
      plot_bgcolor: '#f8f9fa',
      paper_bgcolor: 'white',
      font: { family: 'Poppins' },
      annotations: annotations.length ? annotations : undefined,
    };

    const config: Partial<Plotly.Config> = {
      displayModeBar: true,
      displaylogo: false,
      modeBarButtonsToRemove: ['lasso2d', 'select2d'],
      responsive: true,
    };

    Plotly.newPlot(plotElement, traces, layout, config).then(() => {
      setIsLoading(false);
    });

    return () => {
      Plotly.purge(plotElement);
    };
  }, [combined, slope, isPercentage]);

  if (!combined && !slope) {
    return (
      <div className="cr-flex cr-h-64 cr-items-center cr-justify-center cr-text-gray-500">
        No repeat data available.
      </div>
    );
  }

  return (
    <div className="cr-relative cr-w-full" style={{ height }}>
      {isLoading && (
        <div className="cr-absolute cr-inset-0 cr-z-10 cr-flex cr-items-center cr-justify-center cr-bg-white cr-bg-opacity-75">
          <div className="cr-h-8 cr-w-8 cr-animate-spin cr-rounded-full cr-border-4 cr-border-gray-300 cr-border-t-blue-600" />
        </div>
      )}
      <div ref={plotRef} className="cr-h-full cr-w-full" />
    </div>
  );
}
